import { Link } from '@tanstack/react-router'
import EventPayIcon from '@/assets/EventPay-Icon.svg'
import { Moon, Sun, LogOut, RefreshCw, Menu } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useAuth } from '@/lib/auth'
import { useEvent } from '@/lib/api/queries'
import { useParams } from '@tanstack/react-router'
import { GlobalSearch } from './global-search'
import { Capacitor } from '@capacitor/core'

interface HeaderProps {
  onMenuClick?: () => void
}

export function Header({ onMenuClick }: HeaderProps) {
  const { user, logout, isAuthenticated } = useAuth()
  const params = useParams({ strict: false })
  const eventId = (params as { eventId?: string }).eventId
  const { data: event } = useEvent(eventId ? Number(eventId) : 0)
  
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark'
    }
    return false
  })
  const [autoRefresh, setAutoRefresh] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('autoRefresh') === 'true'
    }
    return false
  })

  const isNative = Capacitor.isNativePlatform()

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark')
      localStorage.setItem('theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('theme', 'light')
    }
  }, [darkMode])

  useEffect(() => {
    localStorage.setItem('autoRefresh', autoRefresh.toString())
  }, [autoRefresh])

  return (
    <div className="sticky top-0 z-40 w-full">
      {isNative && (
        <div
          className="bg-white dark:bg-[#131313]"
          style={{ height: 'var(--safe-area-top)' }}
        />
      )}
      <nav className="bg-white dark:bg-[#131313] border-b border-neutral-200 dark:border-neutral-800">
        <div className="flex justify-between items-center h-16 px-4 sm:px-6 lg:px-8">
        {/* Left side - Event logo for larger screens (above 1400px) */}
        <div className="hidden container:flex items-center space-x-3">
          {event?.logo ? (
            <div className="flex-shrink-0">
              <img
                src={`${event.logo}?width=300`}
                alt={event.name || 'Event'}
                className="w-14 h-14 rounded-md object-contain bg-white dark:bg-neutral-900"
              />
            </div>
          ) : (
            <div className="flex-shrink-0">
              <img
                src={EventPayIcon}
                alt="EventPay"
                className="w-10 h-10 rounded-md"
              />
            </div>
          )}
        </div>

        {/* Menu button and logo (below 1400px) */}
        <div className="container:hidden flex items-center">
          <button
            onClick={onMenuClick}
            className="p-2 -ml-2 mr-2 rounded-md text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 focus:outline-none"
            aria-label="Open navigation menu"
          >
            <Menu className="w-6 h-6" />
          </button>
          {event?.logo ? (
            <div className="flex-shrink-0">
              <img
                src={`${event.logo}?width=200`}
                alt={event.name || 'Event'}
                className="w-10 h-10 rounded object-contain bg-white dark:bg-neutral-900"
              />
            </div>
          ) : (
            <img src={EventPayIcon} alt="EventPay" className="w-9 h-9 rounded" />
          )}
        </div>

        {/* Right side - User info and controls */}
        <div className="flex items-center space-x-1 sm:space-x-3">
          {/* Global Search */}
          <GlobalSearch />
          
          {isAuthenticated && user && (
            <div className="hidden sm:flex flex-col text-right mr-2">
              <span className="truncate text-xs text-neutral-600 dark:text-white">
                {user.first_name} {user.last_name}
              </span>
              <span className="truncate text-xs text-neutral-600 dark:text-white">{user.email}</span>
            </div>
          )}

          {/* Auto-refresh Toggle */}
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`rounded-full p-1.5 sm:p-2 focus:outline-none transition-colors duration-200 ${
              autoRefresh
                ? 'bg-blue-600 text-white hover:bg-blue-700 animate-pulse'
                : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700'
            }`}
            title={autoRefresh ? 'Auto-refresh enabled (10s)' : 'Enable auto-refresh'}
          >
            <RefreshCw className="w-5 h-5" />
          </button>

          {/* Dark Mode Toggle */}
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="rounded-full p-1.5 sm:p-2 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 focus:outline-none transition-colors duration-200"
            aria-label="Toggle dark mode"
          >
            {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>

          {/* Logout */}
          {isAuthenticated ? (
            <button
              onClick={logout}
              className="rounded-full p-1.5 sm:p-2 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors duration-200"
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
          ) : (
            <Link
              to="/auth/login"
              search={{ redirect: '/events', activated: undefined }}
              className="rounded-full p-1.5 sm:p-2 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors duration-200"
            >
              Sign In
            </Link>
          )}
        </div>
        </div>
      </nav>
    </div>
  )
}
